import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { LocaleLink } from '@/components/LocaleLink';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Brand } from '@/components/visual/PagePrimitives';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Calculator, BookOpen, Save, LogOut, Menu, Settings, ClipboardCheck, Users, LayoutDashboard, Scale, Newspaper, LogIn } from 'lucide-react';
import { useState } from 'react';

export function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isActive = path => location.pathname.includes(path);
  const closeDrawer = () => setDrawerOpen(false);
  const handleLogout = async () => {
    closeDrawer();
    await logout();
    navigate(localePath('/'));
  };
  const publicLinks = [
    { path: '/calculator', label: t('nav.calculator'), icon: Calculator },
    { path: '/methodology', label: t('footer.methodology'), icon: Scale },
    { path: '/blog', label: t('footer.blog'), icon: Newspaper },
    { path: '/consultants', label: t('nav.consultants'), icon: Users },
  ];
  const accountLinks = user ? [
    { path: '/assessment', label: t('nav.assessment'), icon: ClipboardCheck },
    { path: '/guidance', label: t('nav.guidance'), icon: BookOpen },
    { path: '/estimates', label: t('nav.estimates'), icon: Save },
    ...(user.is_admin ? [{ path: '/admin/consultants', label: t('nav.admin'), icon: LayoutDashboard }] : []),
  ] : [];
  return <nav className="site-nav" data-testid="main-navbar" aria-label={t('homepage.navigation')}>
    <div className="site-container site-nav-row">
      <Brand testId="nav-brand" />
      <div className="site-nav-links">
        {publicLinks.map(item => <LocaleLink key={item.path} to={item.path} aria-current={isActive(item.path) ? 'page' : undefined}
          data-testid={`nav-desktop-${item.path.slice(1)}`}>{item.label}</LocaleLink>)}
        {!user && <LocaleLink to="/auth" data-testid="nav-signin-btn">{t('nav.signIn')}</LocaleLink>}
      </div>
      <div className="site-nav-controls">
        <LanguageSwitcher variant="dark" />
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <button className="nav-menu-button" data-testid="nav-menu-trigger" aria-label={t('nav.menu')}>
              <Menu size={18} aria-hidden="true" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="site-drawer" data-testid="nav-drawer">
            <SheetTitle className="site-drawer-title">ISMSCalculator</SheetTitle>
            <SheetDescription className="sr-only">{t('nav.menu')}</SheetDescription>
            {user && <div className="nav-user-block" data-testid="nav-user-chip"><p>{t('nav.signedInAs')}</p>
              <strong>{user.is_demo ? t('nav.demo') : user.name || user.email}</strong></div>}
            <div className="site-drawer-links">
              {[...publicLinks, ...accountLinks].map(({ path, label, icon: Icon }) => <LocaleLink key={path} to={path}
                data-testid={`nav-link-${path.slice(1).replaceAll('/', '-')}`} onClick={closeDrawer}
                aria-current={isActive(path) ? 'page' : undefined}><Icon size={18} aria-hidden="true" />{label}</LocaleLink>)}
              {user ? <>
                <LocaleLink to="/settings" onClick={closeDrawer} data-testid="nav-settings-link"><Settings size={18} aria-hidden="true" />{t('nav.settings')}</LocaleLink>
                <button onClick={handleLogout} data-testid="logout-btn"><LogOut size={18} aria-hidden="true" />{t('nav.logout')}</button>
              </> : <LocaleLink to="/auth" onClick={closeDrawer} data-testid="nav-drawer-signin-link"><LogIn size={18} aria-hidden="true" />{t('nav.signIn')}</LocaleLink>}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  </nav>;
}